import type { SynergyDef } from '@origami/shared'

/**
 * Client-side mirror of `packages/server/src/data/synergies.json`.
 *
 * The synergy *logic* (which effect_id fires which simulator effect)
 * lives on the server's authoritative game data. The client needs the
 * `display_name` + `stat_bonus` + `effect_description` strings to render
 * meaningful synergy-row content in the shop panel — without this mirror
 * the panel can only show "need N more for tier X" placeholders, never
 * the actual bonus.
 *
 * TODO: when the server exposes a `GET /api/synergies` route (or when
 * the shared package starts hosting game-data JSON), retire this mirror
 * and import from there. Until then, edits here MUST be kept in sync
 * with the server JSON — pattern: edit one, copy to the other, run
 * `npm run typecheck` to confirm the SynergyDef shape still holds.
 */
export const SYNERGIES: readonly SynergyDef[] = [
  {
    axis: 'animal',
    key: 'bird',
    display_name: 'Bird Flock',
    tiers: [
      { threshold: 2, stat_bonus: { atk: 1 } },
      {
        threshold: 3,
        stat_bonus: { atk: 2 },
        effect_id: 'bird_3_crumple_splash',
        effect_description:
          'When a Bird crumples it deals 1 damage to the enemy frontline.',
        effect_description_short: 'Bird crumple → 1 dmg to enemy front',
      },
      {
        threshold: 5,
        stat_bonus: { atk: 3 },
        effect_id: 'bird_5_rear_strike_on_unfold',
        effect_description: 'The rearmost Bird also strikes once on unfold.',
        effect_description_short: 'Rear Bird strikes on unfold',
      },
    ],
  },
  {
    axis: 'animal',
    key: 'reptile',
    display_name: 'Reptile Coil',
    tiers: [
      { threshold: 2, stat_bonus: { atk: 1, hp: 1 } },
      {
        threshold: 3,
        stat_bonus: { atk: 2, hp: 2 },
        effect_id: 'reptile_3_creased_buff',
        effect_description:
          'First time each Reptile is creased, it gains +1 ATK permanently for the battle.',
        effect_description_short: 'First crease: +1 ATK each Reptile',
      },
      {
        threshold: 5,
        stat_bonus: { atk: 3, hp: 3 },
        effect_id: 'reptile_5_lethal_save',
        effect_description:
          'Reptiles cannot be reduced below 1 HP by a single strike (one-time per Reptile per battle).',
        effect_description_short: 'Reptiles save 1 HP (one-time each)',
      },
    ],
  },
  {
    axis: 'animal',
    key: 'mammal',
    display_name: 'Mammal Pack',
    tiers: [
      { threshold: 2, stat_bonus: { hp: 1 } },
      {
        threshold: 3,
        stat_bonus: { hp: 2 },
        effect_id: 'mammal_3_strike_heal',
        effect_description:
          'When a Mammal strikes, the ally directly behind heals 1 HP.',
        effect_description_short: 'On strike: +1 HP to ally behind',
      },
      {
        threshold: 5,
        stat_bonus: { hp: 3 },
        effect_id: 'mammal_5_unfold_shields',
        effect_description:
          'On unfold all Mammals gain a one-time shield equal to their current HP.',
        effect_description_short: 'Mammals get HP-sized shield on unfold',
      },
    ],
  },
  {
    axis: 'animal',
    key: 'fish',
    display_name: 'Fish School',
    tiers: [
      { threshold: 2, stat_bonus: { hp: 1 } },
      {
        threshold: 3,
        stat_bonus: { hp: 2 },
        effect_id: 'fish_3_frontline_heal',
        effect_description:
          'The frontmost Fish heals 1 HP at the start of each turn.',
        effect_description_short: 'Front Fish: +1 HP per turn',
      },
      {
        threshold: 5,
        stat_bonus: { hp: 3 },
        effect_id: 'fish_5_crumple_team_heal',
        effect_description:
          'When any Fish crumples, all surviving Fish heal 2 HP.',
        effect_description_short: 'Fish crumple → all Fish heal 2',
      },
    ],
  },
  {
    axis: 'animal',
    key: 'insect',
    display_name: 'Insect Swarm',
    tiers: [
      {
        threshold: 2,
        stat_bonus: { atk: 1 },
        effect_id: 'insect_2_amplify_summons',
        effect_description:
          'Mid-battle summons enter play with +3 HP and +3 ATK.',
        effect_description_short: 'Summons: +3 HP / +3 ATK',
      },
      {
        threshold: 3,
        stat_bonus: { atk: 2 },
        effect_id: 'insect_3_amplify_summons_shield',
        effect_description:
          'Mid-battle summons enter play with +5 HP, +5 ATK, and a 1-stack shield.',
        effect_description_short: 'Summons: +5/+5 + shield',
      },
      {
        threshold: 5,
        stat_bonus: { atk: 3 },
        effect_id: 'insect_5_amplify_summons_strike',
        effect_description:
          'Mid-battle summons enter play with +8 HP, +8 ATK, a 2-stack shield, and immediately strike the enemy frontline.',
        effect_description_short: 'Summons: +8/+8 + shield + strike',
      },
    ],
  },
  {
    axis: 'element',
    key: 'wood',
    display_name: 'Wood Grove',
    tiers: [
      { threshold: 2, stat_bonus: { hp: 1 } },
      {
        threshold: 3,
        stat_bonus: { hp: 2 },
        effect_id: 'wood_3_regrowth',
        effect_description:
          'At the start of each turn, the frontmost Wood unit gains +1 HP.',
        effect_description_short: 'Front Wood: +1 HP per turn',
      },
      {
        threshold: 5,
        stat_bonus: { hp: 3 },
        effect_id: 'wood_5_atk_legacy',
        effect_description:
          'When any Wood unit crumples, the rearmost surviving ally gains its remaining ATK as a permanent buff for the battle.',
        effect_description_short: 'Wood crumples → ATK passes to rear ally',
      },
    ],
  },
  {
    axis: 'element',
    key: 'fire',
    display_name: 'Fire Blaze',
    tiers: [
      { threshold: 2, stat_bonus: { atk: 1 } },
      {
        threshold: 3,
        stat_bonus: { atk: 2 },
        effect_id: 'fire_3_splash',
        effect_description:
          "The frontmost ally's strikes deal 1 splash damage to the enemy at position 2.",
        effect_description_short: 'Front strikes: +1 splash to enemy #2',
      },
      {
        threshold: 5,
        stat_bonus: { atk: 3 },
        effect_id: 'fire_5_unfold_aoe',
        effect_description: 'On unfold, deal 2 damage to all enemies.',
        effect_description_short: 'Unfold: 2 dmg to all enemies',
      },
    ],
  },
  {
    axis: 'element',
    key: 'earth',
    display_name: 'Earth Bulwark',
    tiers: [
      { threshold: 2, stat_bonus: { hp: 2 } },
      {
        threshold: 3,
        stat_bonus: { hp: 4 },
        effect_id: 'earth_3_damage_reduction',
        effect_description:
          'The frontmost Earth unit takes 1 less damage from any source (minimum 1).',
        effect_description_short: 'Front Earth: -1 dmg taken (min 1)',
      },
      {
        threshold: 5,
        stat_bonus: { hp: 6 },
        effect_id: 'earth_5_team_shield',
        effect_description:
          'On unfold, the entire team gains a one-time damage shield that absorbs the next strike against each unit.',
        effect_description_short: 'Unfold: team-wide one-time shield',
      },
    ],
  },
  {
    axis: 'element',
    key: 'wind',
    display_name: 'Wind Gust',
    tiers: [
      { threshold: 2, stat_bonus: { atk: 1 } },
      {
        threshold: 3,
        stat_bonus: { atk: 2 },
        effect_id: 'wind_3_double_unfold',
        effect_description:
          "The rearmost ally's on_unfold triggers fire twice.",
        effect_description_short: 'Rear ally unfold fires twice',
      },
      {
        threshold: 5,
        stat_bonus: { atk: 3 },
        effect_id: 'wind_5_unfold_enemy_double_swap',
        effect_description:
          "On unfold, swap two of the enemy team's positions at random, then again — up to four positions changed.",
        effect_description_short: 'Unfold: scramble enemy lineup ×2',
      },
    ],
  },
  {
    axis: 'element',
    key: 'water',
    display_name: 'Water Flow',
    tiers: [
      { threshold: 2, stat_bonus: { hp: 1 } },
      {
        threshold: 3,
        stat_bonus: { hp: 2 },
        effect_id: 'water_3_creased_heal',
        effect_description:
          'When any Water unit is creased, it heals 1 HP.',
        effect_description_short: 'Water creased → +1 HP',
      },
      {
        threshold: 5,
        stat_bonus: { hp: 3 },
        effect_id: 'water_5_lethal_save',
        effect_description:
          'The first time any ally would crumple, it survives at 1 HP instead (one-time per battle).',
        effect_description_short: 'First fatal hit: survive at 1 HP',
      },
    ],
  },
]

/**
 * Lookup helper — finds a synergy def by axis + key. Returns undefined
 * if not registered (e.g., transient mismatch with mirror).
 */
export function findSynergy(
  axis: 'animal' | 'element',
  key: string,
): SynergyDef | undefined {
  return SYNERGIES.find((s) => s.axis === axis && s.key === key)
}

/** Format a stat-bonus block as "+N ATK +M HP" (omits zero/missing). */
export function formatStatBonus(
  bonus: { atk?: number; hp?: number } | undefined,
): string {
  if (!bonus) return ''
  const parts: string[] = []
  if (bonus.atk) parts.push(`+${bonus.atk} ATK`)
  if (bonus.hp) parts.push(`+${bonus.hp} HP`)
  return parts.join(' ')
}

/**
 * Describe a synergy in terms of its CURRENT state given a count:
 *   - active   → "{stat bonus} · {effect description}" of the highest met tier
 *   - inactive → "need N more for tier T"
 *   - capped   → "all bonuses unlocked"
 *
 * Used by the shop synergy sidebar — prefers the snappy
 * `effect_description_short` (~40 chars) when present and falls back
 * to the full `effect_description` otherwise. The codex / hover
 * tooltip read `effect_description` directly so they stay full-length.
 */
export function describeSynergyDetailed(
  axis: 'animal' | 'element',
  key: string,
  count: number,
): string {
  const def = findSynergy(axis, key)
  if (!def) return ''

  const activeTier = [...def.tiers]
    .reverse()
    .find((t) => count >= t.threshold)

  if (activeTier) {
    const bonusText = formatStatBonus(activeTier.stat_bonus)
    const effect =
      activeTier.effect_description_short ?? activeTier.effect_description
    if (bonusText && effect) return `${bonusText} · ${effect}`
    return bonusText || effect || ''
  }

  const nextTier = def.tiers.find((t) => count < t.threshold)
  if (!nextTier) return 'all bonuses unlocked'
  const need = nextTier.threshold - count
  return `need ${need} more for tier ${nextTier.threshold}`
}
